import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
    AppBlockPort,
    SettingOption,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';

export default class ModbusNode extends BaseNode {
    constructor() {
        super();
        this.name = 'Modbus Write';
        this.id = 'node_modbus_register';
        this.linkedFeature = 'general';

        this.inputs = [
            {
                name: 'Write Register',
                portId: 'modbus_poll',
                params: [
                    {
                        name: 'device_id',
                        dataType: TibboDataType.BYTE,
                        default: '',
                    },
                    {
                        name: 'register_address',
                        dataType: TibboDataType.WORD,
                        default: '',
                    },
                    {
                        name: 'data',
                        dataType: TibboDataType.STRING,
                        default: '',
                    },
                ],
            },
        ];

        const settings: Setting[] = [
            {
                id: 'DEVICE',
                name: 'Device',
                settingType: SettingType.OPTION,
                required: false,
                refId: 'modbus_device',
                default: '',
                options: [],
            },
            {
                id: 'SERIAL_PORT',
                name: 'Interface',
                settingType: SettingType.OPTION,
                required: true,
                default: '',
                options: [],
            },
            {
                id: 'FUNCTIONCODE',
                name: 'Function Code',
                settingType: SettingType.OPTION,
                required: true,
                default: 'ReadCoilStatus',
                options: [
                    {
                        name: 'Read COIL/Output (0x01)',
                        value: 'ReadCoilStatus',
                    },
                    {
                        name: 'Read Contact/Discrete Input (0x02)',
                        value: 'ReadInputStatus',
                    },
                    {
                        name: 'Read Holding Register (0x03)',
                        value: 'ReadHoldingRegisters',
                    },
                    {
                        name: 'Read Input Register (0x04)',
                        value: 'ReadInputRegisters',
                    },
                    {
                        name: 'Write Single Coil (0x05)',
                        value: 'WriteSingleCoil',
                    },
                    {
                        name: 'Write Single Register (0x06)',
                        value: 'PresetSingleRegister',
                    },
                ],
            },
            // {
            //     id: 'DEVICEID',
            //     name: 'DeviceID',
            //     settingType: SettingType.STRINGVARIABLE,
            //     dataType: TibboDataType.BYTE,
            //     required: true,
            //     default: '',
            // },
        ];

        this.settings = this.settings.concat(settings);
    }

    getSettingOptions(settingId: string): SettingOption[] | undefined {
        const project: ProjectConfiguration = this.project;
        switch (settingId) {
            case 'SERIAL_PORT':
                return project.getModbusSerialPorts();
            default:
                return super.getSettingOptions(settingId);
        }
    }

    getState(settingId: string): SettingState {
        const project: ProjectConfiguration = this.project;
        const DEVICE = this.getSettingValue('DEVICE');
        switch (settingId) {
            case 'SERIAL_PORT':
                if (DEVICE !== '') {
                    return SettingState.HIDDEN;
                }
                return SettingState.ENABLED;
            default:
                return super.getState(settingId);
        }
    }

    validate(): ValidationMessage[] {
        const errors: ValidationMessage[] = [];

        const SERIAL_PORT = this.getSettingValue('SERIAL_PORT');

        if (!SERIAL_PORT && this.getState('SERIAL_PORT') !== SettingState.HIDDEN) {
            errors.push({
                message: 'A tibbit with a valid serial port must be selected.',
                messageType: ValidationMessageType.ERROR,
                featureId: this.getID(),
                settingId: 'SERIAL_PORT',
            });
        }

        return errors;
    }


    getCodeBlocks(): CodeBlock[] {
        const blocks: CodeBlock[] = [];
        const project: ProjectConfiguration = this.project;

        const DATATYPE = this.getSettingValue('FUNCTIONCODE');
        const DEVICE = this.getSettingValue('DEVICE');
        let PROTOCOL = 'modbus_RTU';
        let INTERFACE = this.getSettingValue('SERIAL_PORT');

        const links = project.getNodeLinks(this, 'modbus_poll');
        if (DEVICE !== '') {
            const device = project.getFeature(DEVICE);
            PROTOCOL = device?.getSettingValue('PROTOCOL');
            if (PROTOCOL === 'modbus_TCP') {
                INTERFACE = `${device?.getSettingValue('NAME')}_MODBUS_SOCK_NUM`;
            } else {
                const feature = project.getFeature(device?.getSettingValue('SERIAL_PORT'));
                if (feature) {
                    INTERFACE = `SER_${feature.getSettingValue('NAME')}`;
                }
            }
        } else if (PROTOCOL === 'modbus_RTU') {
            const feature = project.getFeature(this.getSettingValue('SERIAL_PORT'));
            if (feature) {
                INTERFACE = `SER_${feature.getSettingValue('NAME')}`;
            }
        }


        const doneLinks = project.getNodeLinks(this, 'done');
        const doneBlock = doneLinks.length > 0 ? `'****${doneLinks[0].getSourceId()}****'` : '';

        if (DEVICE === '') {
            blocks.push({
                location: 'on_ser_data_arrival',
                blockType: BlockType.CODE,
                id: `MODBUS_RECV_${INTERFACE}`,
                template: `
if ser.num=${INTERFACE} then
    'modbus master data
    modbus_master_receive(ser.getdata(255),modbus_RTU)
end if`,
            });
        }

        for (let i = 0; i < links.length; i += 1) {
            const link = links[i];
            const DEVICEID = project.getNodeParameter(link, 'device_id');
            const ADDRESS = project.getNodeParameter(link, 'register_address');
            const DATA = project.getNodeParameter(link, 'data');
            blocks.push({
                location: link.getSourceId(),
                blockType: BlockType.CODE,
                id: link.getID(),
                template: `
modbus_master_request(${DEVICEID}, ${DATATYPE}, ${ADDRESS}, 1, modbus_create_data(${DATATYPE},${DATA}), ${INTERFACE}, ${PROTOCOL},0)
${doneBlock}
`,
            });
        }

        return blocks;
    }
}
